import QRCode from 'qrcode';
import { Colonia } from '../../types/colonia';

/* Os modelos em /public/carteirinha-*.png têm 1536x1024; todas as coordenadas
   abaixo estão nesse espaço de pixels, medidas sobre a arte final. */
const W = 1536;
const H = 1024;
const FONT = '"Segoe UI", Arial, Helvetica, sans-serif';
const INK = '#1e2a5e';

export type FaceCarteirinha = 'frente' | 'verso';

/* Medidas por varredura de pixel das molduras/caixas nos templates. */
const FRAME_FRENTE = { x: 172, y: 278, w: 436, h: 503, r: 76 };
const FRAME_RESPONSAVEL = { x: 109, y: 361, w: 343, h: 446, r: 50 };

const loadImage = (src: string, cors = false) =>
  new Promise<HTMLImageElement | null>((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

/* Os templates são os mesmos para todas as crianças; carregamos uma vez só. */
const templateCache = new Map<string, Promise<HTMLImageElement | null>>();
const loadTemplate = (face: FaceCarteirinha) => {
  const key = `/carteirinha-${face}.png`;
  if (!templateCache.has(key)) templateCache.set(key, loadImage(key));
  return templateCache.get(key)!;
};

/* Cada criança tem a mesma foto usada na frente e no verso — buscamos uma vez só
   e reaproveitamos, em vez de baixar o mesmo arquivo duas vezes por carteirinha. */
const photoCache = new Map<string, Promise<HTMLImageElement | null>>();
const loadPhoto = (url: string) => {
  if (!url) return Promise.resolve(null);
  if (!photoCache.has(url)) photoCache.set(url, loadImage(url, true));
  return photoCache.get(url)!;
};

/** Mesmo número em dígitos com DDI 55 na frente, como o link do wa.me espera. */
const paraNumeroWhatsapp = (valor: string) => {
  const numeros = String(valor || '').replace(/\D/g, '');
  if (!numeros) return '';
  if (numeros.length === 10 || numeros.length === 11) return `55${numeros}`;
  return numeros;
};

/* Irmãos costumam compartilhar o telefone do responsável — gera o QR uma vez por
   número, não uma vez por carteirinha. */
const qrCache = new Map<string, Promise<HTMLImageElement | null>>();
const gerarQrWhatsapp = (numeroCompleto: string) => {
  if (!numeroCompleto) return Promise.resolve(null);
  if (!qrCache.has(numeroCompleto)) {
    qrCache.set(
      numeroCompleto,
      QRCode.toDataURL(`https://wa.me/${numeroCompleto}`, {
        width: 320,
        margin: 1,
        color: { dark: '#1e2a5e', light: '#ffffffff' }
      })
        .then((dataUrl) => loadImage(dataUrl))
        .catch(() => null)
    );
  }
  return qrCache.get(numeroCompleto)!;
};

const roundRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

/** Desenha a foto cobrindo o quadro (corte centralizado), com cantos arredondados, ou usando o corte especificado. */
const drawPhoto = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | null,
  nome: string,
  frame: { x: number; y: number; w: number; h: number; r: number },
  crop?: { x: number; y: number; width: number; height: number }
) => {
  ctx.save();
  roundRectPath(ctx, frame.x, frame.y, frame.w, frame.h, frame.r);
  ctx.clip();
  if (img) {
    if (crop) {
      // Usa exatamente a área de recorte definida (em pixels da imagem original)
      // e pinta esticando para cobrir todo o frame (mantendo proporção se o corte for proporcional).
      ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, frame.x, frame.y, frame.w, frame.h);
    } else {
      const scale = Math.max(frame.w / img.width, frame.h / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, frame.x + (frame.w - dw) / 2, frame.y + (frame.h - dh) / 2, dw, dh);
    }
  } else {
    ctx.fillStyle = '#e8f3fb';
    ctx.fillRect(frame.x, frame.y, frame.w, frame.h);
    const iniciais = nome
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((parte) => parte[0])
      .join('')
      .toUpperCase();
    ctx.fillStyle = '#1f6f9f';
    ctx.font = `800 120px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(iniciais || '?', frame.x + frame.w / 2, frame.y + frame.h / 2);
  }
  ctx.restore();
};

const fitFont = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxSize: number,
  minSize = 16
) => {
  // Se minSize > maxSize (ex.: pedir um tamanho menor que o mínimo padrão) o laço
  // nunca rodaria e o canvas ficaria com a fonte "suja" de um desenho anterior.
  const minSizeEfetivo = Math.min(minSize, maxSize);
  for (let size = maxSize; size >= minSizeEfetivo; size -= 1) {
    ctx.font = `700 ${size}px ${FONT}`;
    if (ctx.measureText(text).width <= maxWidth) return;
  }
};

/** Divide em duas linhas pelo ponto mais equilibrado entre as palavras. */
const melhorQuebraDeLinha = (texto: string): [string, string] | null => {
  const palavras = texto.split(/\s+/).filter(Boolean);
  if (palavras.length < 2) return null;
  let melhor: [string, string] = [palavras[0], palavras.slice(1).join(' ')];
  let menorDiferenca = Infinity;
  for (let i = 1; i < palavras.length; i++) {
    const linha1 = palavras.slice(0, i).join(' ');
    const linha2 = palavras.slice(i).join(' ');
    const diferenca = Math.abs(linha1.length - linha2.length);
    if (diferenca < menorDiferenca) {
      menorDiferenca = diferenca;
      melhor = [linha1, linha2];
    }
  }
  return melhor;
};

interface AjusteNome {
  linhas: 1 | 2;
  tamanho: number;
  partes?: [string, string];
}

/**
 * Decide o tamanho da fonte e se precisa quebrar em duas linhas, sem desenhar nada
 * ainda — permite ao chamador saber a altura real antes de posicionar o que vem depois.
 *
 * Prioriza ficar GRANDE: só aceita uma linha só se o tamanho não cair abaixo de
 * `minSizeUmaLinha` (senão duas linhas grandes ficam melhores que uma linha minúscula);
 * quebra em duas linhas tentando o maior tamanho possível; só usa `minSize` (o piso
 * absoluto) como último recurso, para nomes realmente enormes.
 */
const escolherAjusteNome = (
  ctx: CanvasRenderingContext2D,
  texto: string,
  maxWidth: number,
  maxHeight: number,
  maxSize: number,
  minSizeUmaLinha: number,
  minSize: number
): AjusteNome => {
  for (let size = maxSize; size >= minSizeUmaLinha; size -= 1) {
    ctx.font = `800 ${size}px ${FONT}`;
    if (ctx.measureText(texto).width <= maxWidth) return { linhas: 1, tamanho: size };
  }

  const partes = melhorQuebraDeLinha(texto);
  if (partes) {
    for (let size = maxSize; size >= minSize; size -= 1) {
      ctx.font = `800 ${size}px ${FONT}`;
      const alturaLinha = size * 1.18;
      if (
        ctx.measureText(partes[0]).width <= maxWidth &&
        ctx.measureText(partes[1]).width <= maxWidth &&
        alturaLinha * 2 <= maxHeight
      ) {
        return { linhas: 2, tamanho: size, partes };
      }
    }
  }

  for (let size = minSizeUmaLinha - 1; size >= minSize; size -= 1) {
    ctx.font = `800 ${size}px ${FONT}`;
    if (ctx.measureText(texto).width <= maxWidth) return { linhas: 1, tamanho: size };
  }
  return { linhas: 2, tamanho: minSize, partes: partes || [texto, ''] };
};

/**
 * Nome de destaque (ex.: embaixo da foto na frente), alinhado pelo TOPO — para o
 * chamador saber exatamente onde o nome termina e encostar o próximo texto ali.
 * Devolve o Y logo abaixo da última linha desenhada.
 */
const desenharNomeDestaque = (
  ctx: CanvasRenderingContext2D,
  texto: string,
  centerX: number,
  topY: number,
  maxWidth: number,
  maxHeight: number,
  maxSize: number,
  minSizeUmaLinha: number,
  minSize: number,
  color: string
): number => {
  if (!texto) return topY;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const ajuste = escolherAjusteNome(ctx, texto, maxWidth, maxHeight, maxSize, minSizeUmaLinha, minSize);
  ctx.font = `800 ${ajuste.tamanho}px ${FONT}`;

  if (ajuste.linhas === 1 || !ajuste.partes) {
    const alturaLinha = ajuste.tamanho * 1.2;
    ctx.fillText(texto, centerX, topY + alturaLinha / 2);
    return topY + alturaLinha;
  }

  const alturaLinha = ajuste.tamanho * 1.18;
  ctx.fillText(ajuste.partes[0], centerX, topY + alturaLinha / 2);
  ctx.fillText(ajuste.partes[1], centerX, topY + alturaLinha * 1.5);
  return topY + alturaLinha * 2;
};

const drawValue = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  align: CanvasTextAlign,
  size: number,
  color: string = INK
) => {
  if (!text) return;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  fitFont(ctx, text, maxWidth, size);
  ctx.fillText(text, x, y);
};

const formatTelefone = (valor: string) => {
  const numeros = String(valor || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (numeros.length === 11) return { ddd: numeros.slice(0, 2), parte1: numeros.slice(2, 7), parte2: numeros.slice(7) };
  if (numeros.length === 10) return { ddd: numeros.slice(0, 2), parte1: numeros.slice(2, 6), parte2: numeros.slice(6) };
  return { ddd: '', parte1: numeros, parte2: '' };
};

export const gerarCarteirinha = async (
  inscricao: Colonia,
  face: FaceCarteirinha,
  fotoJaCarregada?: HTMLImageElement | null
): Promise<HTMLCanvasElement | null> => {
  const template = await loadTemplate(face);
  if (!template) return null;
  const fotoUrl = face === 'frente' ? inscricao.fotoUrl : inscricao.fotoUrlRetirada;
  const foto = fotoJaCarregada !== undefined ? fotoJaCarregada : await loadPhoto(fotoUrl || '');

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(template, 0, 0, W, H);

  if (face === 'frente') {
    drawPhoto(ctx, foto, inscricao.nomeCompleto, FRAME_FRENTE, inscricao.fotoCrop);
    const centroFoto = FRAME_FRENTE.x + FRAME_FRENTE.w / 2;
    const topoNome = FRAME_FRENTE.y + FRAME_FRENTE.h + 40;
    const gapNomeTurma = 12;
    const tamanhoTurma = 23;
    const reservaTurma = inscricao.turma ? tamanhoTurma * 1.2 + gapNomeTurma : 0;
    const alturaDisponivelNome = H - 18 - reservaTurma - topoNome;

    const fimNome = desenharNomeDestaque(
      ctx,
      inscricao.nomeCompleto.toUpperCase(),
      centroFoto,
      topoNome,
      600,
      alturaDisponivelNome,
      54,
      36,
      22,
      '#ffffff'
    );

    if (inscricao.turma) {
      // Menor e mais clara que o nome, para não se confundir com ele — é só o
      // "subtítulo", encostado logo abaixo da última linha do nome.
      drawValue(
        ctx,
        `Turma ${inscricao.turma}`,
        centroFoto,
        fimNome + gapNomeTurma + tamanhoTurma / 2,
        560,
        'center',
        tamanhoTurma,
        'rgba(255, 255, 255, 0.75)'
      );
    }
    return canvas;
  }

  // Verso: dados de quem está autorizado a retirar a criança.
  const nomeResponsavel = inscricao.nomeRetirada || inscricao.nomeMae || inscricao.nomePai || '';
  drawPhoto(ctx, foto, nomeResponsavel, FRAME_RESPONSAVEL, inscricao.fotoCropRetirada);

  drawValue(ctx, nomeResponsavel, 703, 402, 508, 'left', 26);
  const telefone = formatTelefone(inscricao.telefoneMae || inscricao.telefonePai || '');
  drawValue(ctx, telefone.ddd, 741, 457, 50, 'center', 24);
  drawValue(ctx, telefone.parte1, 844, 457, 124, 'center', 24);
  drawValue(ctx, telefone.parte2, 990, 457, 126, 'center', 24);
  drawValue(ctx, inscricao.parentescoRetirada || '', 729, 517, 482, 'left', 26);

  // QR para chamar o responsável no WhatsApp — no espaço livre à direita da caixa Responsável.
  const numeroWhatsapp = paraNumeroWhatsapp(inscricao.telefoneMae || inscricao.telefonePai || '');
  const qr = await gerarQrWhatsapp(numeroWhatsapp);
  if (qr) {
    const qrTamanho = 150;
    const qrX = 1318;
    const qrY = 366;
    ctx.drawImage(qr, qrX, qrY, qrTamanho, qrTamanho);
    drawValue(ctx, 'Chamar no WhatsApp', qrX + qrTamanho / 2, qrY + qrTamanho + 20, qrTamanho + 40, 'center', 16, '#64748b');
  }

  // Endereço
  const ruaNumero = [inscricao.endereco, inscricao.numero].filter(Boolean).join(', ');
  drawValue(ctx, ruaNumero, 709, 721, 700, 'left', 26);
  drawValue(ctx, inscricao.cidade ? `${inscricao.cidade}${inscricao.uf ? ` - ${inscricao.uf}` : ''}` : '', 645, 770, 764, 'left', 26);
  drawValue(ctx, inscricao.bairro || '', 645, 816, 763, 'left', 26);

  return canvas;
};

/** Converte para PNG; se a foto vier de origem sem CORS o canvas fica "sujo" e regeramos sem foto. */
export const gerarCarteirinhaDataUrl = async (inscricao: Colonia, face: FaceCarteirinha) => {
  const canvas = await gerarCarteirinha(inscricao, face);
  if (!canvas) return null;
  try {
    return canvas.toDataURL('image/png');
  } catch {
    const semFoto = await gerarCarteirinha({ ...inscricao, fotoUrl: '', fotoUrlRetirada: '' }, face);
    return semFoto ? semFoto.toDataURL('image/png') : null;
  }
};

const paraMiniatura = (canvas: HTMLCanvasElement, largura: number) => {
  const mini = document.createElement('canvas');
  mini.width = largura;
  mini.height = Math.round((largura * H) / W);
  const ctx = mini.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, 0, 0, mini.width, mini.height);
  return { mini, ctx };
};

/** Gera as miniaturas de frente e verso baixando cada foto (criança e responsável) uma única vez. */
export const gerarMiniaturasCarteirinha = async (inscricao: Colonia, largura = 320) => {
  const [fotoFrente, fotoVerso] = await Promise.all([
    loadPhoto(inscricao.fotoUrl || ''),
    loadPhoto(inscricao.fotoUrlRetirada || '')
  ]);
  const [frente, verso] = await Promise.all([
    gerarCarteirinha(inscricao, 'frente', fotoFrente),
    gerarCarteirinha(inscricao, 'verso', fotoVerso)
  ]);
  const miniFrente = frente && paraMiniatura(frente, largura);
  const miniVerso = verso && paraMiniatura(verso, largura);
  if (!miniFrente || !miniVerso) return null;
  try {
    return {
      frente: miniFrente.mini.toDataURL('image/jpeg', 0.82),
      verso: miniVerso.mini.toDataURL('image/jpeg', 0.82)
    };
  } catch {
    return null;
  }
};

export const baixarDataUrl = (dataUrl: string, filename: string) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
